import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { scrollToId } from "../../lib/scroll";
import { useLang } from "../../i18n";

export const Services = () => {
  const { c } = useLang();
  const sv = c.services;
  const go = () => scrollToId("#contact");

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-36 bg-[var(--bg-secondary)] relative z-10">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <Overline>{sv.overline}</Overline>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight mt-5 leading-[0.92]">
                {sv.headingA}<br /><span className="italic text-[var(--accent)]">{sv.headingAccent}</span>
              </h2>
            </div>
            <ul data-testid="services-intro-list" className="flex flex-col gap-3 max-w-md lg:max-w-none">
              {sv.introList.map((p) => (
                <li key={p} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-secondary)] leading-relaxed lg:whitespace-nowrap">
                  <span className="h-px w-6 bg-[var(--accent)] shrink-0" />{p}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px mt-14 bg-[var(--border)] border border-[var(--border)]">
          {sv.items.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                data-testid={`accounting-service-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-[var(--bg-primary)] p-8 md:p-10 hover-lift hover:bg-[var(--surface)] cursor-default"
              >
                <Icon size={26} className="text-[var(--accent)]" strokeWidth={1.5} />
                <h3 className="font-serif text-xl md:text-2xl text-[var(--text-primary)] mt-6">{s.title}</h3>
                <p className="font-sans text-sm leading-relaxed text-[var(--text-secondary)] mt-3">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mt-6 md:mt-8">
          {sv.others.map((s) => (
            <Reveal key={s.title}>
              <div
                data-testid={`other-service-${s.tag}`}
                className="group h-full bg-[var(--bg-primary)] border border-[var(--border)] p-10 md:p-14 hover-lift hover:border-[var(--accent)] flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <Overline>{s.tag} — {sv.beyond}</Overline>
                    <ArrowUpRight className="text-[var(--text-secondary)] group-hover:text-[var(--accent)] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-[var(--text-primary)] mt-6">{s.title}</h3>
                  <p className="font-sans text-base leading-relaxed text-[var(--text-secondary)] mt-4 max-w-lg">{s.desc}</p>
                </div>
                <ul className="mt-8 flex flex-col gap-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.12em] text-[var(--text-secondary)]">
                      <span className="h-px w-6 bg-[var(--accent)]" />{p}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`other-service-cta-${s.tag}`}
                  onClick={go}
                  className="mt-8 self-start font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-primary)] link-underline"
                >
                  {sv.enquire}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
